// src/features/treinos/components/muscle-map/muscleMapUtils.js
//
// Utilitários PUROS do MuscleMap + carregamento/tagging do SVG (parse-once).
// Nenhuma string de domínio mora aqui — tudo vem de muscleMapConfig.js.
//
//  • SVG: importado como URL (CRA/file-loader) → fetch raw → parse + tag UMA
//    vez por lado (cache em nível de módulo) → string serializada clonável.
//    O tagging precisa de DOM vivo (path.getBBox()), então roda num host
//    offscreen temporário, idêntico ao prepareMuscleGroups do protótipo.
//  • Derivação: getWorkoutMuscleMap / getPlanMuscleMap / getMuscleRecoveryStatus
//    sempre derivam dos campos do próprio exercício/check-in — sem dicionário
//    grande hardcoded de ativação muscular.

import {
  MUSCLE_SVG_IDS,
  GRADIENT_DEFS_MARKUP,
  STATE_PRIORITY,
  CANONICAL_TO_LOGICAL,
  SVG_VIEWBOX,
  REGION_CROP,
  LOGICAL_REGION,
} from "./muscleMapConfig.js";

import frontSvgUrl from "./assets/muscle-map-front.svg";
import backSvgUrl from "./assets/muscle-map-back.svg";

const SVG_URL = { front: frontSvgUrl, back: backSvgUrl };

// ─────────────────────────────────────────────────────────────────────────────
// CAMPOS DO EXERCÍCIO (fixados na Phase C; fallbacks mantidos por segurança)
// Primário/secundário canônicos vêm do exercise_library/Firestore.
// ─────────────────────────────────────────────────────────────────────────────
export const FIELD_PRIMARY = [
  "muscle_group", "MUSCLE_GROUP", "GRUPO_MUSCULAR",
];
export const FIELD_SECONDARY = [
  "secondary_muscle_groups", "SECONDARY_MUSCLE_GROUPS", "secondary_muscles",
];

// Loga UMA vez as chaves do primeiro exercício real lido, p/ confirmar o casing
// em runtime. TODO remover após validação.
let _loggedExerciseKeys = false;
function debugExerciseKeysOnce(ex) {
  if (_loggedExerciseKeys || !ex || typeof ex !== "object") return;
  _loggedExerciseKeys = true;
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[MuscleMap] chaves do exercício lido:", Object.keys(ex), {
      muscle_group: ex.muscle_group,
      secondary_muscle_groups: ex.secondary_muscle_groups,
    });
  }
}

// Estados atribuídos na derivação (tunáveis).
const PRIMARY_STATE = "focus";
const SECONDARY_STATE = "low";

// Reexportado para quem precisa DISTINGUIR primário de secundário na saída de
// getWorkoutMuscleMap (o check-in do dia grava só os primários). Sem isto a
// string "focus" seria copiada à mão no chamador.
export { PRIMARY_STATE as ESTADO_PRIMARIO, SECONDARY_STATE as ESTADO_SECUNDARIO };

// Janela de recuperação muscular (dias) — tunável.
const RECOVERY_DAYS = 2; // < hoje → trained; < RECOVERY_DAYS → recovery; senão ready

// ─────────────────────────────────────────────────────────────────────────────
// Leitura tolerante de campos
// ─────────────────────────────────────────────────────────────────────────────
function pickField(obj, candidates) {
  if (!obj) return undefined;
  for (const key of candidates) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
      return obj[key];
    }
  }
  return undefined;
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === "") return [];
  if (typeof value === "string") {
    // aceita "Peito, Tríceps" ou "Peito; Tríceps"
    return value.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  }
  return [value];
}

/**
 * Normaliza string canônica p/ casar com as chaves de CANONICAL_TO_LOGICAL.
 * lowercase + deburr (NFD + descarte das marcas combinantes U+0300–U+036F por
 * code point — sem regex com caractere combinante literal no source) +
 * underscores/espaços múltiplos viram 1 espaço (cobre SCREAMING_SNAKE_CASE
 * composto, ex.: "MEMBROS_INFERIORES" → "membros inferiores").
 */
export function normalizeCanonical(value = "") {
  return Array.from(String(value).trim().toLowerCase().normalize("NFD"))
    .filter((ch) => {
      const c = ch.codePointAt(0);
      return c < 0x0300 || c > 0x036f;
    })
    .join("")
    .replace(/[_\s]+/g, " ")
    .trim();
}

// Índice de lookup com OS DOIS lados normalizados: as chaves autoradas em
// CANONICAL_TO_LOGICAL também passam por normalizeCanonical, então uma chave
// futura escrita com acento/caixa ("Tríceps") continua casando. Construído
// UMA vez no load do módulo. CANONICAL_TO_LOGICAL segue sendo o único ponto
// de tradução — isto aqui é só a vista normalizada dele.
const CANONICAL_INDEX = (() => {
  const idx = {};
  for (const [key, logicals] of Object.entries(CANONICAL_TO_LOGICAL)) {
    idx[normalizeCanonical(key)] = logicals;
  }
  return idx;
})();

// warn 1x por string desconhecida (repaint roda com frequência; sem spam).
const warnedUnmapped = new Set();

/**
 * Resolve um muscle_group canônico (qualquer caixa/acento — "PEITO",
 * "Tríceps", "ombros") para o array de chaves lógicas do SVG.
 *   • Array com itens → pinta esses grupos.
 *   • [] → canônico conhecido mas intencionalmente sem pintura (cardio etc.).
 *   • undefined → DESCONHECIDO: loga console.warn (1x por string) para que
 *     qualquer valor canônico novo vindo do banco apareça na hora, em vez de
 *     ficar cinza em silêncio.
 * @param {string} raw valor cru vindo de muscle_group / texto
 * @param {{warn?: boolean}} [opts] warn=false p/ scanning de texto livre
 * @returns {string[]|undefined}
 */
export function resolveCanonicalToLogical(raw, { warn = true } = {}) {
  const norm = normalizeCanonical(raw);
  const logicals = CANONICAL_INDEX[norm];
  if (logicals === undefined && warn && norm && !warnedUnmapped.has(norm)) {
    warnedUnmapped.add(norm);
    // eslint-disable-next-line no-console
    console.warn(
      `[MuscleMap] muscle_group não mapeado: "${raw}" (normalizado: "${norm}")`,
    );
  }
  return logicals;
}

/** Um token canônico só "pinta" se traduzir p/ ≥1 grupo lógico. Silencioso:
 *  roda sobre palavras soltas de títulos de treino — não é caso de warn. */
function isMuscleToken(token) {
  const logical = resolveCanonicalToLogical(token, { warn: false });
  return Array.isArray(logical) && logical.length > 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// RECORTE POR REGIÃO  (prop `region`)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Materializa as frações de REGION_CROP sobre o viewBox original de um lado,
 * devolvendo a string pronta p/ o atributo viewBox do <svg>. Região
 * desconhecida cai em 'full' (viewBox original).
 * @param {"front"|"back"} side
 * @param {"full"|"upper"|"lower"} region
 * @returns {string} "x y w h"
 */
export function getRegionViewBox(side, region) {
  const [X, Y, W, H] = String(SVG_VIEWBOX[side] || "0 0 0 0")
    .split(/\s+/)
    .map(Number);
  const [fx, fy, fw, fh] = REGION_CROP[region] || REGION_CROP.full;
  return `${X + fx * W} ${Y + fy * H} ${fw * W} ${fh * H}`;
}

/**
 * Infere a região pelos grupos lógicos efetivamente pintados (region="auto"):
 * todos upper → 'upper'; todos lower → 'lower'; misto ou vazio → 'full'.
 * @param {Record<string,string>} logicalStates { [logicalKey]: state }
 * @returns {"full"|"upper"|"lower"}
 */
export function inferRegionFromStates(logicalStates = {}) {
  let hasUpper = false;
  let hasLower = false;
  for (const [logical, state] of Object.entries(logicalStates)) {
    if (!state || state === "inactive") continue;
    const r = LOGICAL_REGION[logical];
    if (r === "upper") hasUpper = true;
    else if (r === "lower") hasLower = true;
  }
  if (hasUpper && !hasLower) return "upper";
  if (hasLower && !hasUpper) return "lower";
  return "full";
}

/**
 * Quebra um texto livre (ex.: nome de treino "Peito/Tríceps", "Pernas + Ombros")
 * em tokens canônicos normalizados que casam com CANONICAL_TO_LOGICAL. Descarta
 * conectivos/letras soltas e qualquer palavra que não seja grupo muscular.
 * @param {string} text
 * @returns {string[]} tokens canônicos (ex.: ["peito","triceps"])
 */
export function muscleTokensFromText(text = "") {
  return Array.from(
    new Set(
      String(text)
        .split(/[^\p{L}]+/u) // separa por qualquer não-letra: "/", "+", "-", espaço…
        .map((t) => normalizeCanonical(t))
        .filter((t) => t.length >= 3 && isMuscleToken(t)),
    ),
  );
}

/**
 * Constrói um mapa { [canonical]: state } a partir de texto livre.
 * @param {string} text
 * @param {string} state estado aplicado a cada grupo encontrado (default "focus")
 * @returns {Record<string,string>}
 */
export function getMuscleMapFromText(text, state = "focus") {
  const out = {};
  for (const tok of muscleTokensFromText(text)) out[tok] = state;
  return out;
}

/**
 * Junta os campos textuais CURTOS/ESTRUTURADOS de um treino onde o grupo
 * muscular realmente vive (o gerador de IA não grava muscle_group por exercício
 * — só o NOME_TREINO tipo "Peito/Tríceps" e GRUPO_MUSCULAR no nível da sessão).
 * Usado como fallback quando os exercícios não trazem o grupo.
 *
 * ⚠️ NÃO incluir FOCO_TREINO (frase descritiva longa) nem workout.grupo (que,
 * sem GRUPO_MUSCULAR, vira o próprio FOCO_TREINO): varrer prosa por palavras-
 * músculo acende grupos que NÃO são treinados — ex.: "fortalece a cadeia
 * posterior e os glúteos" num treino só de superiores acendia perna/corpo todo.
 * Melhor subnotificar (não acender) do que acender o corpo inteiro.
 */
function workoutNameText(workout) {
  if (!workout || typeof workout !== "object" || Array.isArray(workout)) return "";
  const raw = workout.raw || workout;
  return [
    workout.nome,
    workout.focoCurto,
    raw.NOME_TREINO,
    raw.GRUPO_MUSCULAR,
    raw.DIVISAO_MUSCULAR,
  ]
    .filter(Boolean)
    .join(" / ");
}

// Warn 1x por exercício sem grupo muscular resolvível (dev) — para mapear depois.
const warnedNoGroup = new Set();

function exerciseList(workout = {}) {
  // Tolerante a vários shapes de "treino": array direto, ou objeto com
  // EXERCICIOS / exercicios / BLOCO_PRINCIPAL / TREINO / exercises.
  if (!workout || typeof workout !== "object") return [];
  if (Array.isArray(workout)) return workout;
  return (
    workout.EXERCICIOS ||
    workout.exercicios ||
    workout.exercises ||
    workout.BLOCO_PRINCIPAL ||
    workout.TREINO ||
    []
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DERIVAÇÃO DE MAPA DE ESTADOS  (chaves = valor canônico PT do exercício)
// O componente traduz canônico→lógico via CANONICAL_TO_LOGICAL na hora de pintar.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Para cada exercício lê o grupo PRIMÁRIO (→ focus) e os SECUNDÁRIOS (→ low),
 * retornando { [canonicalMuscleGroup]: state }. Degrada limpo se só houver
 * primário. Não sobrescreve um "focus" já marcado por um secundário.
 * @param {Array|Object} exercises lista de exercícios (ou treino que a contém)
 * @returns {Record<string,string>}
 */
export function getWorkoutMuscleMap(exercises) {
  const list = exerciseList(exercises);
  const out = {};

  const bump = (canonical, state) => {
    if (!canonical) return;
    const prev = out[canonical];
    if (!prev || (STATE_PRIORITY[state] ?? 0) > (STATE_PRIORITY[prev] ?? 0)) {
      out[canonical] = state;
    }
  };

  for (const ex of list || []) {
    debugExerciseKeysOnce(ex);
    const primary = pickField(ex, FIELD_PRIMARY);
    bump(primary, PRIMARY_STATE);
    for (const sec of asArray(pickField(ex, FIELD_SECONDARY))) {
      bump(sec, SECONDARY_STATE);
    }

    // DEV: exercício SEM grupo primário resolvível → não acende nada (correto),
    // mas logamos UMA vez por nome p/ você mapear o muscle_group depois.
    if (process.env.NODE_ENV !== "production") {
      const resolved = primary
        ? resolveCanonicalToLogical(primary, { warn: false })
        : undefined;
      if (!Array.isArray(resolved) || resolved.length === 0) {
        const nome = ex?.nome || ex?.NOME || ex?.EXERCISE_ID || "(sem nome)";
        if (!warnedNoGroup.has(nome)) {
          warnedNoGroup.add(nome);
          // eslint-disable-next-line no-console
          console.warn(`[MuscleMap] exercício sem grupo muscular mapeado: "${nome}"`);
        }
      }
    }
  }

  // Fallback: exercícios da IA não trazem grupo muscular → deriva do nome/rótulos
  // CURTOS do treino ("Peito/Tríceps", "Costas, Bíceps e Ombro"). NUNCA da prosa
  // (FOCO_TREINO) — ver workoutNameText. Só dispara se nada foi marcado e se
  // `exercises` for o treino normalizado/raw (objeto, não só a lista).
  if (Object.keys(out).length === 0) {
    const text = workoutNameText(exercises);
    if (text) Object.assign(out, getMuscleMapFromText(text, PRIMARY_STATE));
  }
  return out;
}

/**
 * Agrega o mapa de vários treinos do plano (mantém o estado de maior prioridade).
 * @param {Array} workouts
 * @returns {Record<string,string>}
 */
export function getPlanMuscleMap(workouts = []) {
  const out = {};
  for (const w of workouts || []) {
    const map = getWorkoutMuscleMap(w);
    for (const [canonical, state] of Object.entries(map)) {
      const prev = out[canonical];
      if (!prev || (STATE_PRIORITY[state] ?? 0) > (STATE_PRIORITY[prev] ?? 0)) {
        out[canonical] = state;
      }
    }
  }
  return out;
}

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate(); // Firestore Timestamp
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.seconds === "number") {
    return new Date(value.seconds * 1000);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * A partir de check-ins (shape REGISTRO_TREINOS) calcula por músculo o estado
 * recovery/ready/trained com base nos dias desde o último treino daquele grupo.
 * Puro e tolerante a array vazio. Lê grupos via os mesmos FIELD_PRIMARY/SECONDARY
 * dentro de `atividades` (ou `exercicios`) de cada check-in.
 * @param {Array} history lista de check-ins
 * @returns {Record<string,string>}  { [canonicalMuscleGroup]: 'trained'|'recovery'|'ready' }
 */
export function getMuscleRecoveryStatus(history = []) {
  if (!Array.isArray(history) || history.length === 0) return {};

  // último timestamp em que cada grupo foi trabalhado
  const lastTrained = {};
  for (const checkin of history) {
    const when = toDate(checkin?.checkin_at || checkin?.created_at);
    if (!when) continue;
    const itens = checkin?.atividades || checkin?.exercicios || checkin?.EXERCICIOS || [];
    const groups = [];
    for (const item of itens) {
      groups.push(
        pickField(item, FIELD_PRIMARY),
        ...asArray(pickField(item, FIELD_SECONDARY)),
      );
    }
    let filtered = groups.filter(Boolean);

    // Fallback: check-ins não guardam grupo muscular por atividade → deriva do
    // título do treino ("Treino de peito + tríceps", "Pernas/Ombros").
    if (filtered.length === 0) {
      filtered = muscleTokensFromText(
        checkin?.title || checkin?.NOME_TREINO || checkin?.nome || "",
      );
    }

    for (const g of filtered) {
      if (!lastTrained[g] || when > lastTrained[g]) lastTrained[g] = when;
    }
  }

  const now = Date.now();
  const out = {};
  for (const [canonical, date] of Object.entries(lastTrained)) {
    const days = (now - date.getTime()) / 86400000;
    out[canonical] = days < 1 ? "trained" : days < RECOVERY_DAYS ? "recovery" : "ready";
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// ESTIMATIVAS (heurísticas tunáveis)
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_SERIES = 3;
const EXEC_SEC_PER_SET = 40;   // tempo médio de execução por série
const DEFAULT_REST_SEC = 60;   // descanso padrão se o exercício não trouxer ALVO.descanso
const KCAL_PER_MIN = 6;        // gasto médio musculação (tunável)

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

// XP previsto: importe calculateWorkoutBaseXp direto de utils/workoutXp.js nas
// telas. (getPredictedWorkoutXp foi removido na Phase C p/ quebrar o ciclo
// muscleMapUtils ⇄ workoutXp; agora a dependência é unidirecional workoutXp→este.)

/**
 * Estimativa simples de duração: Σ séries × (execução + descanso). Em minutos.
 * @param {Array|Object} workout
 * @returns {number} minutos arredondados
 */
export function getWorkoutDurationEstimate(workout) {
  const list = exerciseList(workout);
  let totalSec = 0;
  for (const ex of list || []) {
    const series = num(ex?.ALVO?.series ?? ex?.series ?? ex?.SERIES, DEFAULT_SERIES) || DEFAULT_SERIES;
    const rest = num(ex?.ALVO?.descanso ?? ex?.descanso ?? ex?.DESCANSO, DEFAULT_REST_SEC) || DEFAULT_REST_SEC;
    totalSec += series * (EXEC_SEC_PER_SET + rest);
  }
  return Math.round(totalSec / 60);
}

/**
 * Estimativa simples de calorias a partir da duração estimada.
 * @param {Array|Object} workout
 * @returns {number} kcal arredondadas
 */
export function getWorkoutCaloriesEstimate(workout) {
  return Math.round(getWorkoutDurationEstimate(workout) * KCAL_PER_MIN);
}

// ─────────────────────────────────────────────────────────────────────────────
// CARREGAMENTO + TAGGING DO SVG  (parse-once, cache por lado)
// Porta prepareMuscleGroups()/isGreyFill() do protótipo (script.js:273/324).
// ─────────────────────────────────────────────────────────────────────────────

/** cinza = canais R/G/B praticamente iguais (linhas/contornos do arquivo). */
function isGreyFill(fill) {
  const m = /^#([0-9a-f]{6})$/i.exec(String(fill).trim());
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return Math.max(r, g, b) - Math.min(r, g, b) < 24;
}

/**
 * Marca grupos nomeados com class="muscle-group" + data-muscle e classifica os
 * paths internos (.m-fill/.m-line/.deco-*). Idêntico ao protótipo; requer que o
 * <svg> esteja anexado ao documento (path.getBBox depende de layout).
 */
function prepareMuscleGroups(svg, side) {
  Object.entries(MUSCLE_SVG_IDS).forEach(([key, ids]) => {
    ids.forEach((rawId) => {
      const scoped = /^(front|back):(.+)$/.exec(rawId);
      if (scoped && scoped[1] !== side) return; // id de outro SVG
      const id = scoped ? scoped[2] : rawId;
      let g = null;
      try {
        g = svg.querySelector(`#${CSS.escape(id)}`);
      } catch {
        g = null;
      }
      if (!g) return; // id pertence ao outro lado
      g.classList.add("muscle-group");
      g.dataset.muscle = key;
    });
  });

  const vb = svg.viewBox.baseVal;
  const veilArea = vb.width * vb.height * 0.02;
  let afterFirstGroup = false;

  svg.querySelectorAll("path").forEach((path) => {
    const fill = path.getAttribute("fill");
    if (fill === null || fill === "none") {
      path.classList.add("deco-stroke");
      return;
    }
    const grey = isGreyFill(fill);
    if (path.closest(".muscle-group")) {
      afterFirstGroup = true;
      path.classList.add(grey ? "m-line" : "m-fill");
      return;
    }
    let b;
    try {
      b = path.getBBox();
    } catch {
      b = { x: 0, y: 0, width: 0, height: 0 };
    }
    const offCanvas =
      b.x < -vb.width * 0.05 ||
      b.y < -vb.height * 0.05 ||
      b.x + b.width > vb.width * 1.05 ||
      b.y + b.height > vb.height * 1.05;
    if (offCanvas || (afterFirstGroup && b.width * b.height > veilArea)) {
      path.classList.add("deco-veil");
      return;
    }
    path.classList.add(grey ? "deco-line" : "deco-shape");
  });
}

// Cache: lado → Promise<string> (markup serializado, tagueado, com defs).
const taggedSvgCache = new Map();

async function buildTaggedSvg(side) {
  const url = SVG_URL[side];
  const res = await fetch(url);
  const raw = await res.text();

  // host offscreen para que getBBox funcione durante o tagging
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText =
    "position:absolute;width:0;height:0;overflow:hidden;left:-9999px;top:-9999px;";
  host.innerHTML = raw;
  document.body.appendChild(host);

  try {
    const svg = host.querySelector("svg");
    if (!svg) return raw;

    // dimensões fluidas: viewBox preservado
    svg.removeAttribute("width");
    svg.removeAttribute("height");
    svg.dataset.view = side;

    prepareMuscleGroups(svg, side);

    // defs de gradiente auto-contidas no topo do próprio SVG
    svg.insertAdjacentHTML("afterbegin", GRADIENT_DEFS_MARKUP);

    return new XMLSerializer().serializeToString(svg);
  } finally {
    document.body.removeChild(host);
  }
}

/**
 * Carrega e tagueia o SVG de um lado UMA vez (cache de módulo). Retorna a string
 * já serializada — instâncias do MuscleMap apenas clonam via innerHTML, sem
 * re-parsear nem rodar getBBox de novo.
 * @param {"front"|"back"} side
 * @returns {Promise<string>}
 */
export function loadTaggedSvg(side) {
  if (!taggedSvgCache.has(side)) {
    taggedSvgCache.set(
      side,
      buildTaggedSvg(side).catch((err) => {
        taggedSvgCache.delete(side); // permite retry numa próxima montagem
        throw err;
      }),
    );
  }
  return taggedSvgCache.get(side);
}
